import { useEffect, useLayoutEffect, useRef, useState } from 'react';

import { ImageSource } from '@/lib/images/cmsImage';

const useIsomorphicLayoutEffect = typeof window === 'undefined' ? useEffect : useLayoutEffect;

export const BATCH_ASSETS_MAX_WAIT_MS = 5000;

export const useBatchAssetsReady = (sources: ImageSource[]): boolean => {
  const [isReady, setIsReady] = useState(() => sources.length === 0);
  const sourcesRef = useRef(sources);

  sourcesRef.current = sources;

  const sourcesKey = sources.map((source: ImageSource) => source.src).join('|');

  useIsomorphicLayoutEffect(() => {
    let isCancelled = false;

    const images = sourcesRef.current.map(({ src, srcSet, sizes }: ImageSource) => {
      const image = new window.Image();

      // srcset/sizes first so the browser picks the same candidate the component
      // will render (and never fetches the bare src fallback on the way).
      if (srcSet) {
        image.sizes = sizes ?? '';
        image.srcset = srcSet;
      }
      image.src = src;

      return image;
    });

    const pending = images.filter((image: HTMLImageElement) => !image.complete);

    if (pending.length === 0) {
      setIsReady(true);

      return;
    }

    let remaining = pending.length;

    const settle = () => {
      remaining -= 1;

      if (!isCancelled && remaining === 0) {
        setIsReady(true);
      }
    };

    pending.forEach((image: HTMLImageElement) => {
      image.onload = settle;
      image.onerror = settle;
    });

    const timeoutId = window.setTimeout(() => {
      if (!isCancelled) {
        setIsReady(true);
      }
    }, BATCH_ASSETS_MAX_WAIT_MS);

    return () => {
      isCancelled = true;
      window.clearTimeout(timeoutId);
    };
  }, [sourcesKey]);

  return isReady;
};
